<template>
  <Transition name="fade">
    <div v-if="isVisible" class="alert-overlay" @click.self="closeAlert">
      <div class="alert-container">
        <div class="alert-header">
          <h3 class="alert-title">{{ title }}</h3>
          <button @click="closeAlert" class="close-button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>

        <div class="alert-content">
          <p class="alert-message">{{ message }}</p>
        </div>

        <div class="alert-footer">
          <button @click="closeAlert" class="confirm-button">
            {{ buttonText }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'

interface AlertProps {
  title: string
  message: string
  buttonText?: string
  autoClose?: boolean
  autoCloseDelay?: number
}

const props = withDefaults(defineProps<AlertProps>(), {
  buttonText: 'OK',
  autoClose: false,
  autoCloseDelay: 0
})

const emit = defineEmits<{
  close: []
}>()

const isVisible = ref(false)

const closeAlert = () => {
  isVisible.value = false
  setTimeout(() => {
    emit('close')
  }, 300)
}

const show = () => {
  isVisible.value = true
}

const hide = () => {
  isVisible.value = false
}

defineExpose({
  show,
  hide
})

// Auto close functionality
watch(isVisible, (visible) => {
  if (visible && props.autoClose && props.autoCloseDelay > 0) {
    setTimeout(() => {
      closeAlert()
    }, props.autoCloseDelay)
  }
})

onMounted(() => {
  // Show alert when component is mounted
  isVisible.value = true
})
</script>

<style scoped>
.alert-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(3px);
}

.alert-container {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 450px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: slideUp 0.3s ease-out;
}

.alert-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ea6666 0%, #e92006 100%);
  color: white;
}

.alert-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.close-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  transition: all 0.2s ease;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.alert-content {
  padding: 24px;
}

.alert-message {
  margin: 0;
  color: #374151;
  font-size: 1rem;
  line-height: 1.5;
}

.alert-footer {
  padding: 16px 24px 24px;
  display: flex;
  justify-content: flex-end;
}

.confirm-button {
  background: linear-gradient(135deg, #914246 0%, #e01e1e 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 32px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.confirm-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.confirm-button:active {
  transform: translateY(0);
}

/* Animations */
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
